import React, { useEffect, useRef, useState } from "react";
import { useAccount, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { BIGCOINMAIN_ADDRESS, Miner } from "../../../utils/constants";
import { notifyError, notifySuccess } from "../../../hooks/utils";
import BigcoinMainContractABI from "../../../abis/bigcoinmain.abi.json";
import "./removeminermodal.css";
import { useTranslation } from "react-i18next";

interface RemoveMinerModalProps {
  open: boolean;
  onClose: () => void;
  miner: {miner: Miner, position: { x: number; y: number }, minerId: number};
}

const RemoveMinerModal: React.FC<RemoveMinerModalProps> = ({ open, onClose, miner }) => {
  const { t, i18n } = useTranslation();
  const account = useAccount();

  const [sellingMiner, setSellingMiner] = useState(false);

  const { data: hash, error, isPending, writeContract } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  // Log the transaction arguments and hash for debugging
  useEffect(() => {
    if (hash) {
      notifySuccess("Selling miner!");
    }
    if (error) {
      notifyError("An error occurred while trying to sell miner.");
    }
    if (isConfirmed) {
      notifySuccess("Miner sold!");
    }
  }, [hash, error]);

  const handleSellMiner = async () => {
    if (!account) {
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setSellingMiner(true);

      const transactionArgs = {
        address: BIGCOINMAIN_ADDRESS,
        abi: BigcoinMainContractABI,
        functionName: 'sellMiner',
        args: [miner.minerId],
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to sell miner.");
    } finally {
      setSellingMiner(false);
    }
  };


  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >

        <div className="sc-aa05954f-3 gnIWBN">
          <div className="sc-5acb55e0-0 fiNBnA">
            <h2 className="sc-5acb55e0-1 fTEMFM">{t("remove_animal.title")}</h2>
            <div className="sc-5acb55e0-2 cORLoW">
              <p className="sc-5acb55e0-3 iUeiNj" style={{ textAlign: "left"}}>
                {t("remove_animal.description")}
              </p>
              <p className="sc-5acb55e0-3 sc-5acb55e0-13 iUeiNj hczxJF" style={{ textAlign: "left"}}>
                {miner.miner.id === 1 ? t("remove_animal.remove_cow_message") : t("remove_animal.remove_animal_message")}
                
              </p>
            </div>
            <div className="sc-5acb55e0-6 VdCDH">
              <div className="sc-5acb55e0-7 blqaxf">
                <div className="sc-5acb55e0-8 brkFc">
                  <img
                    src={miner.miner.img} alt={miner.miner.name}
                    className="sc-5acb55e0-12 dBSxs"
                  />
                </div>
                <div className="sc-5acb55e0-9 dkXnAC">
                  <p className="sc-5acb55e0-10 fhOotK" style={{textAlign: "left"}}>{t("remove_animal.position")}: Tile ({miner.position.x}, {miner.position.y})</p>
                  <p className="sc-5acb55e0-10 fhOotK" style={{textAlign: "left"}}>{t("remove_animal.type")}: {miner.miner.name}</p>
                  <p className="sc-5acb55e0-10 fhOotK" style={{textAlign: "left"}}>{t("remove_animal.milk_power")}: {miner.miner.hashRate} L/s</p>
                  <p className="sc-5acb55e0-10 fhOotK" style={{textAlign: "left"}}>{t("remove_animal.food_required")} {miner.miner.energy} {t("remove_animal.food")}</p>
                  <p className="sc-5acb55e0-10 fhOotK" style={{textAlign: "left"}}>{t("remove_animal.space_required")}: 1 {t("remove_animal.unit")}</p>
                </div>
              </div>
            </div>
            <div className="sc-5acb55e0-11 cOzZOe">
              <button className="sc-cdc39d4e-0 gvrgXY" onClick={onClose}>{t("remove_animal.cancel")}</button>
              <button className="sc-cdc39d4e-0 glfaWa" onClick={handleSellMiner} disabled={sellingMiner || isPending || isConfirming}>{(sellingMiner || isPending || isConfirming) ? t("remove_animal.removing") : t("remove_animal.remove_item")}</button>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
}
export default RemoveMinerModal;